import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { driversAPI } from '../../api/drivers';
import { ordersAPI } from '../../api/orders';
import type { DriverSummary, Order } from '../../types';
import { format } from 'date-fns';
import {
  Truck,
  DollarSign,
  Package,
  CheckCircle,
  Clock,
  MapPin,
} from 'lucide-react';

const DriverDashboard: React.FC = () => {
  const [summary, setSummary] = useState<DriverSummary | null>(null);
  const [assignedOrders, setAssignedOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cashAmount, setCashAmount] = useState<{ [key: number]: string }>({});
  useEffect(() => {
    fetchDriverData();
  }, []);

  const fetchDriverData = async () => {
    setIsLoading(true);
    try {
      // Fetch driver summary
      const summaryResponse = await driversAPI.getDriverSummary();
      if (summaryResponse.data) {
        setSummary(summaryResponse.data);
      }

      // Fetch assigned orders
      const ordersResponse = await driversAPI.getDriverOrders();
      if (ordersResponse.data) {
        setAssignedOrders(ordersResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch driver data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkDelivered = async (orderId: number) => {
    const cash = parseFloat(cashAmount[orderId] || '0');
    if (!cash || cash <= 0) {
      alert('Please enter cash received amount');
      return;
    }

    if (!confirm(`Mark order as delivered with $${cash.toFixed(2)} received?`)) {
      return;
    }

    try {
      const response = await driversAPI.markDelivered(orderId, cash);
      if (response.data) {
        // Update local state
        fetchDriverData();
        setCashAmount(prev => ({ ...prev, [orderId]: '' }));
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to mark order as delivered');
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ElementType;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-lg font-semibold text-gray-900">{value}</dd>
              {subtitle && (
                <dd className="text-sm text-gray-500">{subtitle}</dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout requiredRoles={['driver']}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Driver Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your deliveries and track cash collection
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Today's Deliveries"
            value={summary?.today.deliveries_completed || 0}
            icon={Package}
            color="bg-primary-500"
            subtitle={`${format(new Date(), 'MMM d')}`}
          />
          <StatCard
            title="Cash Collected"
            value={`$${(summary?.today.total_cash_collected || 0).toFixed(2)}`}
            icon={DollarSign}
            color="bg-success-500"
            subtitle="Today"
          />
          <StatCard
            title="Cash on Hand"
            value={`$${(summary?.today.cash_currently_held || 0).toFixed(2)}`}
            icon={DollarSign}
            color="bg-warning-500"
            subtitle="To return to store"
          />
          <StatCard
            title="Pending Deliveries"
            value={summary?.pending.out_for_delivery || 0}
            icon={Truck}
            color="bg-blue-500"
            subtitle="Ready for delivery"
          />
        </div>

        {/* Assigned Orders */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Assigned Deliveries
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Orders marked as "Out for Delivery"
            </p>
          </div>
          
          {assignedOrders.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No deliveries assigned</h3>
              <p className="mt-1 text-sm text-gray-500">
                You don't have any pending deliveries.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cash to Collect
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assignedOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.order_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {format(new Date(order.created_at), 'MMM d')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.customer_name}</div>
                        <div className="text-sm text-gray-500">{order.customer_phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {order.shipping_address?.address_line_1 || 'Address not available'}
                        </div>
                        {order.shipping_address?.city && (
                          <div className="text-sm text-gray-500">
                            {order.shipping_address.city}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${order.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 mr-2">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={cashAmount[order.id] || ''}
                            onChange={(e) => setCashAmount(prev => ({
                              ...prev,
                              [order.id]: e.target.value
                            }))}
                            placeholder={order.total.toFixed(2)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleMarkDelivered(order.id)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-success-600 hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success-500"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mark Delivered
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Completed Deliveries */}
        {summary?.pending.orders && summary.pending.orders.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Upcoming Deliveries
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Next in queue for delivery
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summary.pending.orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.order_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.customer_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800">
                          <Clock className="mr-1 h-3 w-3" />
                          Ready for pickup
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${order.total.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DriverDashboard;